/* Dynamic Plot code copyright 2015-2022 (c) Tuva Labs Inc. */

/**
 * histoElement.js -- Dyn.histoElement() is the controlling object for Histograms in the Tuva Graph.
 * If a Dyn.PlotShaper has Dyn.histoElement(), the plot will build a histogram.
 * Major Changes:
 * 2020-03-17 (Craig) Dyn.HistoElement moved from barElement.js to histoElement.js
 * circa 2015 (Craig) Dyn.HistoElement first created
 */
import ContextMenuUtil from '../../../utils/ContextMenuUtil.js';
import { PlotElement} from './plotElement'

export const wrapDynHistoElement = function(Dyn) {

/**
 * Dyn.HistoElement()
 */
class HistoElement extends PlotElement {

    constructor( Dyn ) {
        super( Dyn );
        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityShape,
            replaceType: Dyn.EPlotControl.kControlsShape,
            primaryType: Dyn.EPlotControl.kControlsHistogram,
            otherControls: [
                Dyn.EPlotControl.kControlsCaseOrder ]
        }, Dyn );

        this.wantStackUp = true; // else stack left-to-right
        this.catStackCount = []; // number of cases in each category
        this.catStackPercent = []; // percent of cases in each category
        this.maxStackCount = NaN;	// number of cases in tallest stack
        this.maxStackPercent = NaN; // percent of cases in tallest stack
        //this.needsDefaultTitle = true;
        this.intervals = null; // {Dyn.dataUtil.calcNiceIntervals||null} histogram intervals
        this.onValueAxis = null; // {*} options for Dyn.AxisView.setPlotSpecificOptions()
        this.onOtherAxis = null; // {*} options for Dyn.AxisView.setPlotSpecificOptions()
        this.currentBarWidth = 0; // temporary value pass from calcPosition() to calcAdornments()
        this.lastNumIntervals = 0;
        this.lastAttributeID = "";
    }

/** upon save keep track of the number of bins */
save() {
    "use strict";
    var plotElementModel = super.save();

    //plotElementModel.primaryType = this.primaryType;
    //plotElementModel.needsDefaultTitle = this.needsDefaultTitle;
    if( this.intervals && this.intervals.numIntervals ) {
        // add the interval details to the model if they exist
        plotElementModel.numIntervals = this.intervals.numIntervals;
    }
    plotElementModel.lastNumIntervals = this.lastNumIntervals;
    plotElementModel.lastAttributeID = this.lastAttributeID;

    return plotElementModel;
}

/** Restore the plot, set the interval details.
 * @param savedData
 */
restore( savedData ) {
    "use strict";
    // restore the histogram interval
    if( savedData.numIntervals ) {
        this.intervals = {};
        this.intervals.numIntervals = savedData.numIntervals;
    }
    //this.needsDefaultTitle = savedData.needsDefaultTitle;
    this.lastNumIntervals = savedData.lastNumIntervals;
    this.lastAttributeID = savedData.lastAttributeID;
}

/**
 * {OVERRIDE}
 * Return true if this (new) plot element should replace the given existing plot element.
 * Override to modify and preserve histogram-related properties.
 * @returns {boolean}
 */
replaces( iExistingPlotElement, ioPlotAdornments ) {
    // if iExistingPlotElement is a count/percent, turn off certain displays incompatible with a new histogram
    if( this.turnOffToggleControl( iExistingPlotElement, this.controlsToTurnOff )) {
        return true; // remove iExistingPlotElement, it has nothing to show
    }
    // if iExistingPlotElement is a histogram, inherit its properties before replacing it.
    if( iExistingPlotElement[Dyn.EPlotControl.kControlsHistogram] && this !== iExistingPlotElement ) {
        this.restore( iExistingPlotElement.save());
        // move outlines from the old histogram, instead of deleting them, so calcAdornments() can animate them
        ioPlotAdornments.renameAdornmentsArray( iExistingPlotElement.primaryType, this.primaryType );
        // return true to replace the old histogram
        return true;
    }
    // else use default behavior
    //return this.replacePlotElement( iExistingPlotElement );
    return super.replaces( iExistingPlotElement, ioPlotAdornments );
}

toString() {
    "use strict";
    return "{HistoElement}";
}

/**
 * Find the axis to use as the numeric histogram axis, or return null if we should just stack up cases in each bin.
 * @param xAxisView {Dyn.AxisView}
 * @param yAxisView {Dyn.AxisView}
 * @returns  {Dyn.AxisView|null}
 */
getValueAxis( xAxisView, yAxisView ) {
	// we default to using the X axis, if both X and Y are numeric. Return null if neither works.
    // the value axis will be numeric or null
	return( xAxisView.isNumericActiveOrInactive() ? xAxisView : ( yAxisView.isNumericActiveOrInactive() ? yAxisView : null ));
}
getNonValueAxis( xAxisView, yAxisView ) {
    // return the other axis if there is a value axis, otherwise null.
    return( xAxisView.isNumericActiveOrInactive() ? yAxisView : ( yAxisView.isNumericActiveOrInactive() ? xAxisView : null ));
}

getFutureValueAxis( xAxisView, yAxisView ) {
    // get either X or Y axis, where the histogram scale will go. Rules:
    // 1. if one axis is numeric, use that numeric axis,
    // 2. else if one axis is categorical, use the non-categorical (null) axis
    // 3. else use the X axis (i.e. default to X if both numeric or both categorical, or both null ).
    let valueAxis = this.getValueAxis( xAxisView, yAxisView ),
        isOnlyXCategorical = xAxisView.isCategoricalActiveOrInactive() && ! yAxisView.isCategoricalActiveOrInactive();
    return valueAxis || (isOnlyXCategorical ? yAxisView : xAxisView );
}
getFutureNonValueAxis( xAxisView, yAxisView ) {
    // get either X or Y axis, which ever is the cross-axis for where the histogram scale will go
    // this axis may or may not have a valid category attribute to form the cross-axis.
    let nva = this.getNonValueAxis( xAxisView, yAxisView ),
        isOnlyXCategorical = xAxisView.isCategoricalActiveOrInactive() && ! yAxisView.isCategoricalActiveOrInactive();
    return nva || (isOnlyXCategorical ? xAxisView : yAxisView );
}

getFrequencyAxis( xAxisView, yAxisView ) {
    // return the axis to be used as the frequency axis (frequency scale), or null;
    // returns a non-null axis only if there is a value axis, and the flag "wantFrequencyAxis" is set.
    return( this.onOtherAxis.wantFrequencyAxis ? this.getNonValueAxis( xAxisView, yAxisView ) : null );
}
getPercentAxis( xAxisView, yAxisView ) {
    // return the axis to be used as the percent axis (percent scale, sum to 100%), or null;
    // returns a non-null axis only if there is a value axis, and the flag "wantPercentAxis" is set.
    return( this.onOtherAxis.wantPercentAxis ? this.getNonValueAxis( xAxisView, yAxisView ) : null );
}
getProportionAxis( xAxisView, yAxisView ) {
    // return the axis to be used as the proportion axis (scale where proportions sum to 1), or null;
    // returns a non-null axis only if there is a value axis, and the flag "wantPercentAxis" is set.
    return( this.onOtherAxis.wantProportionAxis ? this.getNonValueAxis( xAxisView, yAxisView ) : null );
}
getMaxStackPercentOrProportion( isPercent ) {
    return isPercent ? this.maxStackPercent : (this.maxStackPercent/100);
}

/**
 * Update the plot's axis model, removing any elements incompatible with this PlotElement.
 * Sets:
 * 	this.wantStackUp
 * 	this.needsDefaultTitle
 * 	this.onValueAxis
 * 	this.onOtherAxis
 * Handles the following type of axis configurations (test each!)
 *  1. empty X and Y axis : add prompts and marks X axis as needing a numeric attribute.
 *  2. categorical attribute on X, empty on Y, or vise versa: marks empty axis as needing a numeric attribute.
 *  3. categorical attribute on X and Y: disables categorical attribute on X, marks X as needing a numeric attribute.
 *  4. numeric attribute on X, empty on Y, or vise versa: mark as complete plot, frequency axis on empty (non-numeric) axis.
 *  5. numeric attribute on X, categorical on Y, or vise versa: complete plot (with no scale on non-numeric axis)
 *  6. numeric on both X and Y: complete plot, disable numeric attribute(s) on Y.
 *  7. Complete plot (i.e. 5 or 6) and more than one numeric attribute on an axis: disable all but first numeric attribute.
 */
calcAxis( ioWhatChanged, subViewRef ) {
    let xAxisView = subViewRef.xAxisView, yAxisView = subViewRef.yAxisView,
        topMargin = subViewRef.topMargin, rightMargin = subViewRef.rightMargin;

    let valueAxis = this.getValueAxis(xAxisView, yAxisView),
        otherAxis = valueAxis && (valueAxis.isHorizontal ? yAxisView : xAxisView),
        isIncompletePlot = Boolean(!(xAxisView.isNumericActiveOrInactive() || yAxisView.isNumericActiveOrInactive())),
        //isOnlyXCategorical = xAxisView.isCategoricalActiveOrInactive() && ! yAxisView.isCategoricalActiveOrInactive(),
        futureValueAxis = this.getFutureValueAxis( xAxisView, yAxisView ),
        futureOtherAxis = this.getFutureNonValueAxis( xAxisView, yAxisView );

    // stack cases bottom to top if (1) no valueAxis, or (2) value axis on X, else (3) value Axis on Y
    this.wantStackUp = futureValueAxis.isHorizontal;

    // update axis prompt, if any, and "incomplete axis" styling if applicable.
    if( futureValueAxis.isEmptyOfAttributes()) {
        // add/update prompt to default value axis, if axis is empty, and no numeric attribute in the plot
        futureValueAxis.setTitle(Dyn.loc(Dyn.plotStrings.promptValue), isIncompletePlot);
    }
    if( futureOtherAxis.isEmptyOfAttributes()) {
        // add prompt on other axis if empty and value has a prompt, otherwise just clear other axis of any prompt
        let otherPrompt = futureValueAxis.isEmptyOfAttributes() ? Dyn.loc(Dyn.plotStrings.promptGroup) : "";
        futureOtherAxis.setTitle( otherPrompt, false );
    }

    // update default titles once on plot creation, then let user customize (don't change on recalc)
    this.plotShaper.setDefaultPlotTitle( subViewRef.topMargin, Dyn.loc( this.kDefaultTitle ));

	// set up custom axis endpoints and hide unused numeric axes
    if( valueAxis ) {
        // axis types have changed, we must update our axis stats right now.
        //valueAxis.postCalcAxisUpdate(); // make sure valueAxis.allAttribues.stats is correct for valid plotRange
        //otherAxis.postCalcAxisUpdate(); // make sure otherAxis.stats.categories is up-to-date.

        // deactivate unused numeric attributes, if any
        let numAttributesUsedValueAxis = Math.min( 1, valueAxis.allAttributes.count() ), // 1 or 0 attributes
            numAttributesUsedOtherAxis =( otherAxis.isNumericActiveOrInactive() ? 0 : Math.min( 1, otherAxis.allAttributes.count())),
            suppressAxisScale = otherAxis.isCategoricalActiveOrInactive(); // hide frequency, percent, or proportion axis
            //userRequestedPercentBySection = this.plotShaper.hasElement( Dyn.EPlotControl.kControlsSectionPercentage );

        this.onValueAxis = {
            isIncompleteAxisStyle: false,
            numActiveAttributes: numAttributesUsedValueAxis,
            showPlusMinus: true,
            disablePlusButton: this.intervals?.isAtMax,
            disableMinusButton: this.intervals?.isAtMin,
        };
        this.onOtherAxis = {
            isIncompleteAxisStyle: false,
            numActiveAttributes:numAttributesUsedOtherAxis, // 2nd numeric axis is ignored
            wantFrequencyAxis: (this.primaryType === Dyn.EPlotControl.kControlsFreqHistogram) && !suppressAxisScale,
            wantProportionAxis: (this.primaryType === Dyn.EPlotControl.kControlsPropHistogram) && !suppressAxisScale,
            wantPercentAxis: (this.primaryType === Dyn.EPlotControl.kControlsPercHistogram)  && !suppressAxisScale,
        };
        valueAxis.setPlotSpecificOptions( this.onValueAxis );
        otherAxis.setPlotSpecificOptions( this.onOtherAxis );
    } else {
        // remove old axis customizations; prepare for selecting the value axis
        Dyn.assert( isIncompletePlot );
        this.onValueAxis = {
            isIncompleteAxisStyle: true,
            numActiveAttributes: 0 };
        this.onOtherAxis = {
            isIncompleteAxisStyle: false,
            numActiveAttributes: futureOtherAxis.allAttributes.count() };
        futureValueAxis.setPlotSpecificOptions( this.onValueAxis );
        futureOtherAxis.setPlotSpecificOptions( this.onOtherAxis );
    }
    topMargin.setPlotSpecificOptions( null );
    rightMargin.setPlotSpecificOptions({ "disableAttribute":isIncompletePlot });
    // NOTE: setPlotSpecificOptions() called again in calcPlotSpecificRange()
}

/**
 * Record which cases that have missing values for the given plot.
 */
calcMissingValueCases( numCases, xAxisView, yAxisView ) {
	var missingCases = this.plotShaper.dataSet.missingCases;
    const usePreFilterStats = true, // so that filtered-out categories are not marked as missing (they should be marked as filtered only)
		xAttrIndex = 0,
		yAttrIndex = 0;

    xAxisView.postCalcAxisUpdate(); // update axis type if changed during calcAxis()
    yAxisView.postCalcAxisUpdate();

	for( let i=0; i<numCases; i++ ) {
		let x = xAxisView.getCaseDataAsNumber( i, xAttrIndex, usePreFilterStats ),
		    y = yAxisView.getCaseDataAsNumber( i, yAttrIndex, usePreFilterStats ),
		    // exclude missing values on X and Y (e.g. blank categorical value, non-numeric on numeric axis)
            isValid = isFinite( x ) && isFinite( y );
		missingCases.setCaseIsMissing( i, ! isValid );
	}
}

/**
 * Adjust the axis endpoints, depending on the bins in the histogram.
 * This step is take after CalcAxis() and CalcMissingValueCases() so that missing cases are correct,
 * but before calcPosition() and calcAdornments() so they have updated axis viewa to position histogram.
 * @param xAxisView
 * @param yAxisView
 */
calcPlotSpecificRange( numCases, subViewRef ) {
	let valueAxis = this.getValueAxis( subViewRef.xAxisView, subViewRef.yAxisView ),
        nonValueAxis = this.getNonValueAxis( subViewRef.xAxisView, subViewRef.yAxisView ),
        frequencyAxis = this.getFrequencyAxis( subViewRef.xAxisView, subViewRef.yAxisView ),
        percentAxis = this.getPercentAxis( subViewRef.xAxisView, subViewRef.yAxisView ),
        proportionAxis = this.getProportionAxis( subViewRef.xAxisView, subViewRef.yAxisView ),
        cellMarginXY = this.plotShaper.getCellMargin(); // estimated text size to reserve for count/percent displays

	if( valueAxis ) {
		// if filters were changed or especially if missing value cases just changed
		// (prior call to calcMissingValueCases()), we must update our axis stats right now.
		valueAxis.postCalcAxisUpdate(); // make sure valueAxis.allAttribues.stats is correct for valid plotRange

		// extend the axis endpoints to include the numeric range of the first attribute
		// and deactivate other attributes if any (2nd+ attribute)
		let firstAttribute = valueAxis.getAttribute(),
			plotRange = new Dyn.mathUtil.Range( firstAttribute.stats.min, firstAttribute.stats.max ),
			numIntervals = (this.intervals ? this.intervals.numIntervals : Dyn.plotUtil.kStartingNumIntervals),
            intervalOptions = { irLowerOutside:true, irUpperBeyond:true }, // histogram intervals are 'outside' of data (first tick <= data <= last tick), see Dyn.EIntervalRange
            preserveLastIntervals = !!this.intervals && (this.lastNumIntervals === this.intervals.numIntervals &&
                    this.lastAttributeID === firstAttribute.id);
        if( preserveLastIntervals ) {
            // if updating a histogram with the same number of intervals and same attribute, preserve the exact interval width.
            // This avoids width wandering too far when many filtering or sampling changes to the data (issue #1176)
            // but also allows for a totally new interval width when changing attributes
            intervalOptions.exactWidth = this.intervals.intervalWidth;
            intervalOptions.isAtMin = this.intervals.isAtMin;
            intervalOptions.isAtMax = this.intervals.isAtMax;
        }
		plotRange.makeNonZeroWidth(); // in case all values are missing or equal
		this.intervals = Dyn.dataUtil.calcNiceIntervals( plotRange.min, plotRange.max, numIntervals, intervalOptions );
        this.lastNumIntervals = this.intervals.numIntervals; // save for next update to histogram
        this.lastAttributeID = firstAttribute.id; // save for next update to histogram
        this.calcMaxStackCount( valueAxis, nonValueAxis );
		plotRange.min = this.intervals.min;
		plotRange.max = this.intervals.max;

        // now save and set the axis options
        this.onValueAxis.plotMinMax = plotRange;
        this.onOtherAxis.marginForSum = valueAxis.isHorizontal ? cellMarginXY.y : cellMarginXY.x; // e.g. if vertical bars, add space for count on Y
		if( frequencyAxis ) {
		    Dyn.assert( frequencyAxis === nonValueAxis );
		    this.onOtherAxis.plotMinMax = new Dyn.mathUtil.Range( 0, this.maxStackCount );
        } else if( percentAxis || proportionAxis ) {
            Dyn.assert( percentAxis === nonValueAxis || proportionAxis === nonValueAxis );
            this.onOtherAxis.plotMinMax = new Dyn.mathUtil.Range( 0, this.getMaxStackPercentOrProportion( percentAxis!==null ) );
        }
        valueAxis.setPlotSpecificOptions( this.onValueAxis );
		nonValueAxis.setPlotSpecificOptions( this.onOtherAxis );
	} else {
	    // get max stack count+percent for incomplete axis
        Dyn.assert( this.onValueAxis.isIncompleteAxisStyle );
        let futureOtherAxis = this.getFutureNonValueAxis( subViewRef.xAxisView, subViewRef.yAxisView );
        this.calcMaxStackCount( null, futureOtherAxis );
        this.intervals = null;
        this.onOtherAxis.marginForSum = futureOtherAxis.isHorizontal ? cellMarginXY.x : cellMarginXY.y; // e.g. if vertical stack, add space for count on Y
        futureOtherAxis.setPlotSpecificOptions( this.onOtherAxis );
    }
}

/**
 * Change icon style to Rectangle (default is a circle)
 * @param ioPositions
 * @param xAxisView
 * @param yAxisView
 */
calcIconStyle( ioPositions /*, xAxisView, yAxisView*/ ) {
    //console.log("HistoElement.calcIconStyle");
    var numCases = ioPositions.length; // bar width may be determined by number of cases
	this.plotShaper.iconStyler.setStyleRectangle( numCases );
}

/** update the case positional data (shared by all elements) and adornments (specific to this element) */
calcPosition( ioPositions, xAxisView, yAxisView /*, ioAdornments*/ ) {
	//console.log("HistoElement.calcPosition");
	var iconStyler = this.plotShaper.iconStyler,
		dataSet = this.plotShaper.dataSet,
        xIsSeparated = xAxisView.isCategoricalActiveAxis(),
        yIsSeparated = yAxisView.isCategoricalActiveAxis(),
		plotCells = this.plotShaper.bins.cells,
		numCells = plotCells.length,
		numCases = ioPositions.length,
		sortingAttribute = dataSet.getOrderByAttribute(),
		i, p;

	// get case values, missing values, and bin locations {xBinIndex, yBinIndex, cellIndex}
	for( i=0; i<numCases; i++ ) {
		// calculate initial case position
		p = ioPositions[i];
		p.caseIndex = i;
		p.x = xAxisView.getCaseDataAsNumber( i );
		p.y = yAxisView.getCaseDataAsNumber( i );
		p.isVisible = dataSet.isCaseNonMissingNonFiltered( p.caseIndex );
		p.xAttrIndex = 0;
		p.yAttrIndex = 0;
		this.calcBinIndex( p, xIsSeparated, yIsSeparated );

		// convert from data coordinates (numeric value, category index, etc) to view coordinates (pixel position)
		p.x = xAxisView.dataToPixel( p.x );
		p.y = yAxisView.dataToPixel( p.y );
		if( !(p.isVisible)) { // position all invisible cases offscreen
			p.x = iconStyler.missingValuePosition.x;
			p.y = iconStyler.missingValuePosition.y;
			p.width = p.height = Dyn.plotUtil.missingCaseSize;
		}
	}
	this.populateCellsWithCases( ioPositions );

	// position all visible cases as stacked blocks, one cell at a time
    let valueAxis = this.getValueAxis( xAxisView, yAxisView),
        frequencyAxis = this.getFrequencyAxis( xAxisView, yAxisView ),
        percentAxis = this.getPercentAxis( xAxisView, yAxisView ),
        proportionAxis = this.getProportionAxis( xAxisView, yAxisView );
	for( i=0; i<numCells; i++ ) {
		if( valueAxis ) {
			this.positionHistogramBlocksInCell( ioPositions, plotCells[i], sortingAttribute, valueAxis,
                frequencyAxis, percentAxis, proportionAxis, i );
		} else {
			this.positionStackOfBlocksInCell( ioPositions, plotCells[i], sortingAttribute );
		}
	}
}

/**
 * Update the adornments array for the histogram element;
 *  We make one rectangle adornment to outline each histogram bar.
 *  Expecting [0 or 1] histogram per cell, 1+ bars per histogram, 1+ cases per bar, from calcPositions()
 */
calcAdornments( ioAdornArray, casePositions, xAxisView, yAxisView, noAnimation ) {
    //console.log("HistoElement.calcAdornments", ioAdornArray.length, "old adornments");
    const clipRectString = Dyn.plotView.getPlotInteriorClipRect(),
        animationTime = (noAnimation ? Dyn.plotUtil.noAnimationMs : Dyn.plotUtil.stdAnimationTimeMs),
        strokeWidth = Math.min( Dyn.plotUtil.histogramWidth, this.currentBarWidth * Dyn.plotUtil.histogramMaxStrokeToBarWidth );

    let adornmentIndex = 0;
    this.plotShaper.bins.cells.forEach( (cell, cellIndex, cellsArray) => {
        Dyn.assert( cell.histogramData ); // created by calcPosition() subroutines positionHistogramBlocksInCell() and positionStackOfBlocksInCell(
        cell.histogramData.barEnds.forEach( (barRect, barIndex) => {
            if( barRect.caseCount > 0 ) { // ignoring bars of zero height
                // get the matching adornment and check to see if it is from our current cell
                //      if not, remove it, so we don't move (animate) bars between cells
                let barOutline = ioAdornArray[adornmentIndex],
                    deleteCount = 0;
                while( barOutline && (barOutline.data("cellIndex") < cellIndex) ) {
                    ++deleteCount;
                    barOutline = ioAdornArray[adornmentIndex + deleteCount];
                }
                deleteAdornmentsAt( adornmentIndex, deleteCount); // remove any from previous cells

                if( (!barOutline) || barOutline.data("cellIndex") !== cellIndex ){
                    // create a new barOutline to outline this histogram bar
                    barOutline = this.plotShaper.adornments.paper.rect(
                        barRect.x, barRect.y, barRect.width, barRect.height)
                        .attr({
                            'stroke-width': strokeWidth,
                            'stroke': Dyn.plotUtil.histogramStroke,
                            'opacity': 0
                        });
                    ioAdornArray.splice( adornmentIndex, 0, barOutline );
                }
                // update the location of this barOutline
                barOutline.animate({
                    x: barRect.x, y: barRect.y, width: barRect.width, height: barRect.height,
                    'stroke-width': strokeWidth,
                    'opacity': 1
                }, animationTime, Dyn.plotUtil.stdAnimationEasing)
                    .attr( 'clip-rect', clipRectString )
                    .data("cellIndex", cellIndex)
                    .data("barIndex", barIndex); // for possible future use
                ++adornmentIndex;
            }
        });
    });

    // remove any unused bar outlines from last plot (no animation, immediate remove)
    deleteAdornmentsAt( adornmentIndex, ioAdornArray.length - adornmentIndex );
    Dyn.assert( adornmentIndex === ioAdornArray.length ); // assert that we have scanned all adornments

    function deleteAdornmentsAt( adornmentIndex, deleteCount ) {
        if( deleteCount > 0 ) {
            let deletedRects = ioAdornArray.splice(adornmentIndex, deleteCount); // remove from adornments
            deletedRects.forEach(r => r.remove()); // remove from paper
        }
    }
}

/**
 * Get the number of cases in the highest stack in the plot, to scale the frequency axis,
 * percentage axis, or bar length appropriately.
 *
 * Updates:
 *  this.maxStackCount
 *  this.maxStackPercent
 *
 * NOTE: do not rely on number of cases in cells, this is a subroutine of calcAxis()
 * or calcPlotSpecificRange(); cases in each cell not updated until calcPosition() stage.
 * @param valueAxis {Dyn.AxisView|null} - numeric histogram axis or null (see getValueAxis())
 * @param crossAxis {Dyn.AxisView} - the non-histogram axis (see getFutureValueAxis())
 */
calcMaxStackCount( valueAxis, crossAxis ) {
    Dyn.assert( crossAxis );
    const getMax = (a, b) => Math.max(a, b);
	let dataSet = this.plotShaper.dataSet,
		numCategories = Math.max( 1, ( crossAxis.isCategoricalAxis() ? crossAxis.allAttributes.stats.numCategories() : 0 )), // default of 1 category
        categoryCaseCounts = Array(numCategories).fill(0);

	if( valueAxis ) {
        // we can form a valid histogram; so count the cases within each
        // (categorical axis bin, numeric interval bin) pair, and get the max count.
	    Dyn.assert( crossAxis && this.intervals && this.intervals.values );
	    let numericCaseValues = valueAxis.getAttribute().cases,
            numIntervals = this.intervals.values.length,
            categoryIntervalCaseCounts = categoryCaseCounts.map( c => Array( numIntervals).fill(0) );
        numericCaseValues.forEach( (numericValue, caseIndex) => {
            if (dataSet.isCaseNonMissingNonFiltered(caseIndex)){
                let intervalIndex = Dyn.dataUtil.calcIntervalIndex( this.intervals.values, numericValue ),
                    categoryIndex = (numCategories > 1) ? crossAxis.getCaseDataAsNumber(caseIndex) : 0;
                if( intervalIndex >= 0 && categoryIndex >= 0 ) { // if in any interval
                    ++(categoryIntervalCaseCounts[categoryIndex][intervalIndex]);
                    ++(categoryCaseCounts[categoryIndex]); // also update total count in each category
                    //maxCount = Math.max( maxCount, newCount );
                }
            }
        });
        this.catStackCount = categoryIntervalCaseCounts.map( ( array ) => array.reduce( getMax, 0 ));
        this.catStackPercent = categoryIntervalCaseCounts.map( ( array, arrayIndex ) => {
            let maxStackCount = array.reduce( getMax, 0 ),
                catCount = categoryCaseCounts[arrayIndex];
            return (catCount > 0) ? (100 * (maxStackCount / catCount)) : 0;
        });
        this.maxStackCount = this.catStackCount.reduce( getMax, 0 );
        this.maxStackPercent = this.catStackPercent.reduce( getMax, 0 );
	} else {
	    // we cannot yet form a histogram, so count the number of non-missing cases
        //      (within each categorical axis bin if present), to get the max count.
        let numCases = dataSet.getNumCasesAfterFilter();
		for( let caseIndex=0; caseIndex < numCases; caseIndex++ ){
            if( dataSet.isCaseNonMissingNonFiltered(caseIndex) ){
                let categoryIndex = (numCategories > 1) ? crossAxis.getCaseDataAsNumber(caseIndex) : 0;
                if( categoryIndex >= 0 ){ // if in any interval
                    ++(categoryCaseCounts[categoryIndex]);
                    //maxCount = Math.max( maxCount, newCount );
                }
            }
        }
        this.maxStackCount = categoryCaseCounts.reduce( getMax, 0 );
        this.maxStackPercent = 100; // even if no cases
	}
	Dyn.assert( this.maxStackCount >= 0 && this.maxStackPercent >= 0 );
}

/**
 * Update the case positional data for cases in the given cell,
 * positioning them as a single stack of rectangles in the cell.
 * Updates:
 *      this.currentBarWidth
 * @param ioPositions -- case rectangle position: { x, y, width, height}
 * @param cell -- cell to update.
 * @param sortingAttribute
 */
positionStackOfBlocksInCell( ioPositions, cell, sortingAttribute ) {
	let size = this.getBarConstants( {}, false ),
        numCasesInCell = cell.cases.length,
		numCasesWide = 1,
		cellMaxLength = Math.max(
		    cell.widthOrHeight( ! this.wantStackUp ) * size.kMinBarLengthProp,
            (cell.widthOrHeight( ! this.wantStackUp ) - this.onOtherAxis.marginForSum) * size.kBarLengthStacked ),
		cellMaxWidth = cell.widthOrHeight( this.wantStackUp ),
		barStartX = cell.centerX(),
		barStartY = cell.centerY(),
		barLength, barWidth, barWidthMargin,
		i, caseIndex, p, displayWidth, stackOffset;

	// compute bar widths and heights (which remain fixed unless we have value bars)
	barLength = stackOffset = cellMaxLength * Dyn.mathUtil.invertSafely( this.maxStackCount );
	barWidth = cellMaxWidth;
	displayWidth = barWidth * size.kBarWidthSingle;
	barWidthMargin = (barWidth - displayWidth)/ 2; // 1/2 of distance between bars
	displayWidth = Math.max( size.kMinBarWidthPixels, displayWidth ); // bars may overlap to be visible if very very small
    this.currentBarWidth = Math.abs( displayWidth );

	if ( sortingAttribute ) {
		this.sortCasesInCell( cell, sortingAttribute, ioPositions, ! sortingAttribute.isSortAscending );
	}
    // save cell.histogramData for count or percent displays of each bar
    Dyn.assert( this.intervals === null ); // only used when we have no value attribute and no intervals
    cell.histogramData = {
        stackHorizontal: this.wantStackUp, // true for horizontal numeric axis
        intervals: null, // frequency counts
        barEnds: [] // bar position
    };

	// compute starting positions of bars in cell
	if( this.wantStackUp ) {
		barStartX -= (0.5 *((numCasesWide)*barWidth)) - barWidthMargin; // offset to find center of first bar
		barStartY = cell.bottom() - size.kCellMargin;	// uniform bars start at bottom edge of cell
	} else {
		barStartY += (0.5 *((numCasesWide)*barWidth)) + barWidthMargin - barWidth; // offset to find center of first bar
		barStartX = cell.left() + size.kCellMargin;		// uniform bars start at left edge of cell
	}

	// set position of each case bar {x,y,height,width}, stacking as we go.
	for ( i = 0; i < numCasesInCell; i++ ) {
		caseIndex = cell.cases[i];
		Dyn.assertArrayIndex( ioPositions, caseIndex );

		p = ioPositions[caseIndex];
		if( this.wantStackUp ) {
			p.x = barStartX;
			p.y = barStartY - barLength;
			p.width = displayWidth;
			p.height = barLength; // bar length goes vertically
			barStartY -= stackOffset;
		} else {
			p.x = barStartX;
			p.y = barStartY;
			p.width = barLength; // bar length goes horizontally
			p.height = displayWidth;
			barStartX += stackOffset;
		}
		Dyn.mathUtil.normalizeRect( p ); // handle bars in negative directly correctly
	}
}

getBarConstants( size, hasValueAxis ) {
    size.kBarLengthStacked = 0.9;  // stacked frequency bars use this proportion of available space in cell
    size.kMinBarWidthPixels = 0.1; // bars are at least one pixel wide to be visible in the cell, even if overlapping
    size.kCellMargin = 2;	// pixels
    if( hasValueAxis ) {
        size.kCellSpace = 0.9;	// unstacked (spread) bars use this proportion of available space between cells
    } else {
        size.kBarWidthSingle = 0.5; // if only one bar (stacked or one case per cell), use this proportion of available width between bars
        size.kMinBarLengthProp = 0.5; // max bar length is at least 50% of cell width in the case of small cells with large count/percent text
    }
    return size; // for chaining
}

calcHistogramBarSize( cell, cellIndex, valueAxis, frequencyAxis, percentAxis, proportionAxis ) {
    let size = this.getBarConstants( {}, true ),
        valueAttribute = valueAxis.getAttribute(),
        cellStackHeight = cell.widthOrHeight( ! this.wantStackUp ) * size.kCellSpace * size.kBarLengthStacked,
        percOrPropAxis = percentAxis || proportionAxis || null;
    Dyn.assert( !((percentAxis!==null)&&(proportionAxis!==null)), "Can't have both proportion and percent axis at the same time");

    // bar length = number of pixels for a 1-case-high block, in this stacking direction (this.wantStackUp)
    // bar width = number of pixels width of bar in non-stacking direction
    let percentsPerCase = NaN,
        barLength; // height of a 1-case-high block...
    if( frequencyAxis ) {
        // ...on Frequency axis scale
        barLength = frequencyAxis.coordMap.scaleAbs(1);
    } else if( percOrPropAxis ) {
        // ...on Percent or Proportion axis scale
        percentsPerCase = this.getMaxStackPercentOrProportion( percentAxis!==null ) * Dyn.mathUtil.invertSafely( this.maxStackCount );
        barLength = percOrPropAxis.coordMap.scaleAbs( percentsPerCase );
    } else if( this.hasRelativeScale ) {
        // ...on Category axis, with absolute scale (equal length bars if same freq across all categories)
        barLength = cellStackHeight * (this.catStackPercent[cellIndex]/100) * Dyn.mathUtil.invertSafely( this.catStackCount[cellIndex]);
    } else {
        // ...on Category axis, with relative histogram scale (equal length bars if same percent/proportion across all categories)
        barLength = cellStackHeight * Dyn.mathUtil.invertSafely( this.maxStackCount );
    }
    let idealBarWidth = valueAxis.coordMap.scaleAbs( this.intervals.intervalWidth ),
        displayDirection = ( this.wantStackUp === valueAttribute.isSortAscending ) ? 1 : -1;

    // add more sizes
    size.barLength = barLength;
    size.barWidth = displayDirection * Math.max( size.kMinBarWidthPixels, idealBarWidth ); // bars may overlap to be visible if very, very small
    size.percOrPropAxis = percOrPropAxis;
    size.percentsPerCase = percentsPerCase;
    return size;
}

/**
 * Update the case positional data for the given cell,
 * arranging case icons as a histogram of stacked rectangles.
 * Uses this.intervals to form bars along the value axis.
 * Updates:
 *      this.currentBarWidth
 * @param ioPositions {array} -- case rectangle position: { x, y, width, height}
 * @param cell -- cell to update.
 * @param sortingAttribute {Dyn.Attribute}
 * @param valueAxis {Dyn.AxisView}-- reference to X or Y axis view that has numeric attribute and numeric intervals
 * @param frequencyAxis {Dyn.AxisView|null}-- reference to X or Y axis view that has frequency scale (or null if no frequency scale)
 * @param percentAxis {Dyn.AxisView|null}-- reference to X or Y axis view that has percent scale (or null if no percent
 * @param proportionAxis {Dyn.AxisView|null}-- reference to X or Y axis view that has percent scale (or null if no percent scale)
 */
positionHistogramBlocksInCell( ioPositions, cell, sortingAttribute, valueAxis, frequencyAxis, percentAxis, proportionAxis, cellIndex ) {
    let size = this.calcHistogramBarSize( cell, cellIndex, valueAxis, frequencyAxis, percentAxis, proportionAxis ),
        valueAttribute = valueAxis.getAttribute(),
        reverseIntervalOrder = valueAttribute.isSortAscending===false; // show intervals in reverse order in Summary view
    this.currentBarWidth = Math.abs( size.barWidth );

    // save cell.histogramData for count or percent displays of each bar
    cell.histogramData = {
        stackHorizontal: this.wantStackUp, // true for horizontal numeric axis
        valueAttributeID: valueAttribute.id,
        maxStackCount: this.maxStackCount,
        barEnds: [] // center of bar end position { x, y } for each bar.
    };

	if( sortingAttribute ) {
		this.sortCasesInCell( cell, sortingAttribute, ioPositions, ! sortingAttribute.isSortAscending );
	}

	// compute starting positions of bars in cell
    let blockStartMap;
	if( this.wantStackUp ) {
		let barStartY = cell.bottom() - size.kCellMargin;	// uniform bars start at bottom edge of cell
        if( frequencyAxis ) {
            blockStartMap = frequencyAxis.coordMap.clone();
        } else if( size.percOrPropAxis ) {
            blockStartMap = size.percOrPropAxis.coordMap.clone();
            blockStartMap.scale *= size.percentsPerCase;
        } else {
            blockStartMap = new Dyn.mathUtil.CoordMap( barStartY, barStartY - size.barLength, 0, 1 );
        }
	} else {
		let barStartX = cell.left() + size.kCellMargin;		// uniform bars start at left edge of cell
        if( frequencyAxis ) {
            blockStartMap = frequencyAxis.coordMap.clone();
        } else if( size.percOrPropAxis ) {
            blockStartMap = size.percOrPropAxis.coordMap.clone();
            blockStartMap.scale *= size.percentsPerCase;
        } else {
            blockStartMap = new Dyn.mathUtil.CoordMap( barStartX, barStartX + size.barLength, 0, 1 );
        }
	}

	// position ioRect to show the position of one case in the histogram; multiple cases form a bar.
    // the rectangle is one unit high in stacking direction (barLength),
    // and the width of the interval in the cross-direction (barWidth)
    function positionBar( ioRect, stackCount, intervalStart, wantStackUp /*, freqOrPercentAxis*/ ) {
        //var stackOffset = intervals.caseCounts[ intervalIndex ]++;
        //console.log( "positionBar", wantStackUp, blockStartFunc( stackCount ), blockStartMap.transform( stackCount ));
        if( wantStackUp ) {
            ioRect.x = valueAxis.dataToPixel( intervalStart ); // min value of interval here
            ioRect.y = blockStartMap.transform( stackCount + 1 );
            ioRect.width = size.barWidth;
            ioRect.height = size.barLength; // bar length goes vertically
        } else {
            ioRect.x = blockStartMap.transform( stackCount );
            ioRect.y = valueAxis.dataToPixel( intervalStart ); // min value of interval here
            ioRect.width = size.barLength; // bar length goes horizontally
            ioRect.height = size.barWidth;
        }
        Dyn.mathUtil.normalizeRect( ioRect ); // handle bars in negative directly correctly
        Dyn.assertValidRect( ioRect );
    }

	// initialize the interval data: zero cases in each interval, and bar ends at zero
    let barRect;
	this.intervals.caseCounts = Dyn.dataUtil.filledArray( this.intervals.values.length, 0 );
	const g = Dyn.dataUtil.getGapForIntervalWidth( valueAttribute.getNumericDecimals(), this.intervals.intervalWidth );
    for( let i=0, j=this.intervals.values.length; i<j; i++) {
        barRect = new Dyn.mathUtil.Rect();
        positionBar( barRect, 0, this.intervals.values[i], this.wantStackUp );
        barRect.intervalString = valueAttribute.numericIntervalToString( this.intervals.values[i], this.intervals.intervalWidth, g.gap, g.decimals );
        barRect.caseCount = 0;
        cell.histogramData.barEnds.push( barRect );
    }
    if( reverseIntervalOrder ) {
        cell.histogramData.barEnds.reverse();
    }

	// set position of each case bar {x,y,height,width}, stacking as we go.
    let sortValues = [],
        numIntervals = this.intervals.values.length;
    cell.cases.forEach( (positionIndex, caseArrayIndex) => {
		Dyn.assertArrayIndex( ioPositions, positionIndex );
		let p = ioPositions[positionIndex],
            caseValue = valueAttribute.cases[p.caseIndex],
            intervalIndex = Dyn.dataUtil.calcIntervalIndex( this.intervals.values, caseValue ),
            barEndIndex = reverseIntervalOrder ?  numIntervals - intervalIndex - 1 : intervalIndex;
		if( intervalIndex >= 0 ) { // if in any interval
            // position case at end of bar
            positionBar( p, this.intervals.caseCounts[ intervalIndex ]++, this.intervals.values[intervalIndex], this.wantStackUp );
            // update bar size and count for count/percent displays
            barRect = cell.histogramData.barEnds[ barEndIndex ];
            barRect.caseCount = this.intervals.caseCounts[ intervalIndex ];
            barRect.union( new Dyn.mathUtil.Rect( p.x, p.y, p.width, p.height ));
		} else {
			p.x = p.y = p.width = p.height = 0; // not missing, but out of range of intervals
			Dyn.assert( intervalIndex >= 0, "need to exclude this case from cell"); // calcAxisEndpoints() should have extended intervals to covered this value
		}
        // save the sort order values for this case icon
        if( Dyn.plotUtil.enableCaseTabOrder ) {
            sortValues.push({
                index: p.caseIndex, // same as case icon index; p.iconIndex !== p.caseIndex when multiple attributes
                value: intervalIndex,  // primary sort is by index of the stack on axis
                value2: barRect.caseCount }); // secondary sort is by height index in stack
        }
	});

    // now resort case icons in this cell, to get correct tabindex order
    if( Dyn.plotUtil.enableCaseTabOrder ) {
        Dyn.dataUtil.sortCaseIndices( sortValues, cell.cases );
    }
    Dyn.assert( this.intervals.values.length === cell.histogramData.barEnds.length );
}

/**
 * Alows each element to respond to an axis button click
 * @param ioWhatChanged -- element should add a Dyn.EPlotChange parameter if returning true.
 * @param subViewRef
 * @param axisdata
 * @returns {boolean} -- true if handled by a Dyn.PlotElement
 */
handleAxisButtonClick( ioWhatChanged, subViewRef, axisData ) {
	"use strict";
	var handled = false,
		//valueAxis = this.getValueAxis( subViewRef.xAxisView, subViewRef.yAxisView ),
		isPlusButton  = (axisData.type==="plusButton"),
		isMinusButton = (axisData.type==="minusButton"),
        wantMoreIntervals = axisData.wantUndoOfClick ? ! isPlusButton : isPlusButton;

	if( ( isPlusButton || isMinusButton ) && this.intervals ) {
        // update the number of intervals; new intervals will be calculated later by calcPlotSpecificRange()
        Dyn.plotUtil.setNumIntervalsPlusMinus( this.intervals, wantMoreIntervals );
		ioWhatChanged[ Dyn.EPlotChange.kChAxisSize ] = true; // intervals affect axis range which affects axis view size.
		ioWhatChanged[ Dyn.EPlotChange.kChIconPosition ] = true; // icons must be positioned in new intervals
		handled = true;
		// track analytics on histogram bin update
		_gaq.push(['_trackEvent', 'Plot Function', 'Click', "Bins Updated"]);
		console.log("HistoElement.handleAxisButtonClick numIntervals=", this.intervals.numIntervals );
	}
	return handled;
}

/**
 * Do we allow counting of sections(individual bars) for our histogram(s)?
 * Used by other plot elements to determine what histogram is doing.
 * @returns {boolean}
 */
allowHistogramSectionCounts() {
    "use strict";
    // allow counts or percents, unless the histogram is incomplete; see calcAxis()
    return ! this.onValueAxis.isIncompleteAxisStyle;
    //return( this.intervals !== null ); // true if we have intervals indicating a numeric attribute is present (see this.calcAxisOptions())
}

/**
 * Get the human-readable names of the histogram bins
 * Note that if there are N histogram bins on X (or Y), and M category bins on Y (or X), there will be (N x M) bin names.
 * But for now all histograms use the same bins so just return the names of the N bins.
 */
getBinNames() {
    "use strict";
    var plotCells = this.plotShaper.bins.cells,
        numCells = plotCells.length,
        whichCell = 0,
        namesArray = [];

    if( numCells > 0 ) {
        var histogramData = plotCells[whichCell].histogramData;
        if ( histogramData.barEnds ) {
            // each histogram might have bars
            histogramData.barEnds.forEach( function ( barData, index ) {
                namesArray.push( barData.intervalString );
            } );
        }
    }
    return namesArray;
}

displayOrderBy() {
    "use strict";
    return false; // because we use Dyn.dataSet.getOrderByAttribute() for plot
}

/**
 * Get plot sonification data for the Histogram Graph
 * @returns {{caseIndices: [], numNotes: number, type: string, noteIndex: number}}
 */
getPlotSonificationData() {
    let soundData = {
        type: 'dataRects',
        dataRects: [],
        dataValues: [],
        dataMin: 0,
        dataMax: this.maxStackCount,
        //attributeID: "",
        //numNotes : 0,
        notes: [],
    };
    let plotCells = this.plotShaper.bins.cells;
    // get non-missing case indices in line graph order, as sorted by calcAdornments()
    plotCells.forEach( (cell) => {
        if( cell.histogramData && cell.histogramData.barEnds ) {
            cell.histogramData.barEnds.forEach( (barRect) => {
                let v = barRect.caseCount,
                    r = new Dyn.mathUtil.Rect( barRect );
                if( v===0 ) {
                    v = Dyn.dataUtil.kNumericMissingValue;
                    if( this.wantStackUp ) {
                        r.y += r.height; // move zero-height stacks down one unit for visualization
                        r.height = Dyn.plotUtil.sonifyBorderWidth * 3;
                    } else {
                        r.width = Dyn.plotUtil.sonifyBorderWidth * 3;
                        r.x -= r.width; // move zero-height stacks to the left one unit for visualization
                    }
                }
                let note = Dyn.sound.makeNote().numericValueToNote( v, soundData.dataMin, soundData.dataMax );
                soundData.dataRects.push( r );
                soundData.dataValues.push( v );
                soundData.notes.push( note );
            });
        }
    });
    //soundData.numNotes = soundData.dataValues.length;
    return soundData;
}

/** Do we want the plot to show a context menu in the plot title? */
contextMenuOptions() {
    return {
        wantContextMenu:true,
        menuID: Dyn.plotUtil.histogramContextMenuID,
        menuToolTip: 'histogram menu'
    };
}

getContextMenuClickHandler() {
    return HistoElement.handleContextMenuClick;
}

/**
 * Handle a click on the plot title's context menu.
 * @param event -- keyboard or mouse event
 * @param buttonElement -- the SVG element that invoked the event
 * @param plotElement
 */
static handleContextMenuClick( event, buttonElement, plotElement ) {
    let xy = Dyn.plotUtil.clickOrKeyboardEventToViewportXY( event, buttonElement );
    plotElement = null; // forget reference; plotElement will be removed from plot if axisType change
    setTimeout( () => { // delayed call is required to show when e.type==='keydown', reason unknown
        ContextMenuUtil.show( {x:xy.x, y:xy.y}, {
                // HistoGramContextMenu component props
                getHistState: function( histoElement ) { return {
                    axisType: histoElement.getHistAxisType(),
                    binOptions: histoElement.getHistBinOptions()
                }}
            },
            ( code ) => {
                // HistoGramContextMenu.props.handleChange() callback
                if( code && code.change==="axisType" ) {
                    HistoElement.setHistAxisType( code.axisType, Dyn );
                } else if( code && code.change==="binType" ) {
                    HistoElement.toggleHistBinOption( code.binType /*, code.wantOn*/ );
                }
            }, Dyn.plotUtil.histogramContextMenuID, Dyn );
    }, 100 );
}

getHistAxisType() {
    switch( this.primaryType ) {
        case Dyn.EPlotControl.kControlsFreqHistogram:
            return 'freq';
        case Dyn.EPlotControl.kControlsPercHistogram:
            return 'perc';
        case Dyn.EPlotControl.kControlsPropHistogram:
            return 'prop';
        default:
            Dyn.assert( false, "Invalid Histogram control type: "+this.primaryType );
            return; // undefined
    }
}

/** set histogram axis type;
 * note that this static method removes the current HistoElement via the HistoElement.replaces() method. */
static setHistAxisType( axisType, Dyn ) {
    let i = HistoElement.initData[axisType];
    Dyn.assert( i, "unknown axis type:"+axisType );
    Dyn.mediator.publish( "plot:contextMenuChange", {
        action: 'addPlot',
        primaryType: i.primaryType,
        extraType: i.extraType,
        baseType: i.baseType,
        //noAnimation: true,
    });
}

/**
 * Get the histogram bin options for showing count/percent/proportion. Note that we query
 * the plotShaper because other elements are responsible for the count/percent/proportion
 * model and display.
 * @returns {{binCount: boolean, binProportion: boolean, binPercent: boolean}}
 */
getHistBinOptions() {
    return {
        binCount: this.plotShaper.hasElement( Dyn.EPlotControl.kControlsSectionCount ),
        binPercent: this.plotShaper.hasElement( Dyn.EPlotControl.kControlsSectionPercentage ),
        binProportion: this.plotShaper.hasElement( Dyn.EPlotControl.kControlsSectionProportion )
    }
}

static binOptionData = {
    // NOTE: must be synchronized with ToolbarComponent.menuValueDict for now,
    // so menu and context menu both work the same way --CDM 2022-08-22
    binCount: {
        controlType: Dyn.EPlotControl.kControlsSectionCount,
        baseType: Dyn.EPlotControl.kControlsCount,
        actionName: "Count by Histogram Bin",
    },
    binPercent: {
        controlType: Dyn.EPlotControl.kControlsSectionPercentage,
        baseType: Dyn.EPlotControl.kControlsPercentage,
        actionName: "Percent by Histogram Bin",
    },
    binProportion: {
        controlType: Dyn.EPlotControl.kControlsSectionProportion,
        baseType: Dyn.EPlotControl.kControlsPercentage,
        actionName: "Proportion by Histogram Bin",
    }
}

static toggleHistBinOption( binType /*, wantOn*/ ) {
    // tell the plot view to change the given count/percent/proportion element
    let i = HistoElement.binOptionData[binType];
    Dyn.assert( i, "unknown bin type:"+binType );
    Dyn.mediator.publish( "plot:contextMenuChange", {
        action: 'togglePlot',
        controlType: i.controlType,
        baseType: i.baseType,
        actionName: Dyn.loc( i.actionName ),
        //noAnimation: true,
    });
    //Dyn.assert( Dyn.plotView.plotShaper.hasElement( i.controlType ) === wantOn );
}

static initData = {
    // NOTE: must be synchronized with ToolbarComponent.menuValueDict for now,
    // so menu and context menu both work the same way --CDM 2022-08-22
    freq: {
        primaryType: Dyn.EPlotControl.kControlsFreqHistogram,
        title: Dyn.plotTitles.FREQ_HISTOGRAM,
        controlsToTurnOff: [
            Dyn.EPlotControl.kControlsSectionProportion,
            Dyn.EPlotControl.kControlsSectionPercentage
        ],
        extraType: Dyn.EPlotControl.kControlsSectionCount,
        baseType: Dyn.EPlotControl.kControlsCount
    },
    perc: {
        primaryType: Dyn.EPlotControl.kControlsPercHistogram,
        title: Dyn.plotTitles.REL_PERC_HISTOGRAM,
        controlsToTurnOff: [
            Dyn.EPlotControl.kControlsSectionCount,
            Dyn.EPlotControl.kControlsSectionProportion
        ],
        extraType: Dyn.EPlotControl.kControlsSectionPercentage,
        baseType: Dyn.EPlotControl.kControlsPercentage
    },
    prop: {
        primaryType: Dyn.EPlotControl.kControlsPropHistogram,
        title: Dyn.plotTitles.REL_PROP_HISTOGRAM,
        controlsToTurnOff: [
            Dyn.EPlotControl.kControlsSectionCount,
            Dyn.EPlotControl.kControlsSectionPercentage
        ],
        extraType: Dyn.EPlotControl.kControlsSectionProportion,
        baseType: Dyn.EPlotControl.kControlsPercentage
    }
};

/** initialize a histogram */
initHistogram( axisType, Dyn ) {
    let data = HistoElement.initData[ axisType ];
    Dyn.assert( data, "initHistogram invalid axis type: "+axisType );
    this.initElement( {
        priority: Dyn.EPlotPriority.kPriorityShape,
        replaceType: Dyn.EPlotControl.kControlsShape,
        primaryType: data.primaryType,
        otherControls: [
            Dyn.EPlotControl.kControlsHistogram,
            Dyn.EPlotControl.kControlsCaseOrder ]
    }, Dyn );
    this.kDefaultTitle = data.title;
    this.kUndoAction = data.title;
    this.controlsToTurnOff = data.controlsToTurnOff;
}

} // class HistoElement
Dyn.HistoElement = HistoElement;
Dyn.PlotElemHelper.registerOldType( Dyn.EPlotControl.kControlsHistogram, (savedData, partialPlotState) => {
    let percentageElementSavedData = partialPlotState.plotElements.find(
            (el) => (el.primaryType === Dyn.EPlotControl.kControlsPercentage)),
        hasPercentByHistogramSection = percentageElementSavedData && percentageElementSavedData[Dyn.EPlotControl.kControlsSectionPercentage];
    // Old Histogram with Percent by Histogram Section on? - convert to Relative Percentage Histogram, else convert to Frequency Histogram
    return hasPercentByHistogramSection ? Dyn.RelPercentHistoElement :Dyn.FreqHistoElement;
} );

/****************************************************/

// controller for a Frequency Histogram, always has a Frequency axis
class FreqHistoElement extends HistoElement {

    constructor( Dyn ) {
        super( Dyn );
        this.initHistogram( 'freq', Dyn );
    }
    get hasRelativeScale() { return false }

} // class FreqHistoElement
Dyn.FreqHistoElement = FreqHistoElement;
Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsFreqHistogram, Dyn.FreqHistoElement );

/****************************************************/

    // controller for a Relative Percentage Histogram, always has a Percentage axis
class RelPercentHistoElement extends HistoElement {

    constructor( Dyn ) {
        super( Dyn );
        this.initHistogram( 'perc', Dyn );
    }
    get hasRelativeScale() { return true }

} // class RelPercentHistoElement
Dyn.RelPercentHistoElement = RelPercentHistoElement;
Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsPercHistogram, Dyn.RelPercentHistoElement );

/****************************************************/

// controller for a Relative Frequency Histogram, always has a Proportional axis
class RelProportionHistoElement extends HistoElement {

    constructor( Dyn ) {
        super( Dyn );
        this.initHistogram( 'prop', Dyn );
    }
    get hasRelativeScale() { return true }

} // class RelProportionHistoElement
Dyn.RelProportionHistoElement = RelProportionHistoElement;
Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsPropHistogram, Dyn.RelProportionHistoElement );

}; // wrapDynHistoElement
